// https://km.sankuai.com/page/163352667
import { reportError } from '../owl'
import isWebpSupported, { webpSupportedStatus } from './webp'

export interface I_THUMB_CONFIG {
  w?: number
  h?: number
  at?: boolean
  e?: number
  c?: number
  Q?: number
  noKnbMedia?: boolean
  suffix?: string
  fixedRatio?: number
  maxRatio?: number
  [x: string]: string | number | boolean | undefined
}

const defaultOptions = {
  e: 1,
  c: 1,
  Q: window.lowNetwork ? 60 : 90,
  fixedRatio: undefined,
  maxRatio: 3,
  noKnbMedia: true,
}

const deviceRatio = (function getDeviceRatio() {
  if (window.lowNetwork) return 1
  if (!window.devicePixelRatio) return 1
  return window.devicePixelRatio > 3 ? 3 : window.devicePixelRatio
})()

// console.log('[deviceRatio]', deviceRatio)

/**
 * 将venus的图片url转换为指定大小的缩略图
 * options = {
 *    w:  指定目标缩略图的宽度 (1-4096)
 *    h:  指定目标缩略图的高度 (1-4096)
 *    e:  缩放优先边 0 -- 长边优先(默认), 1 -- 短边优先, 2 -- 强制缩放成目标大小
 *        如原图200 * 400（比例1:2），需要缩放为100 * 100（比例1:1）.
 *        长边优先时，缩放为50 100；短边优先时(e=1)，缩放为`100 200`，若不特别指定，则代表长边优先
 *    c:  是否自动裁剪 1 -- 自动裁剪 0 -- 不自动裁剪
 *    l:  目标缩略图大于原图是否处理。如果值是1, 即不处理，是0，表示处理
 *    q:  决定jpg图片的相对quality，对原图按照q%进行quality压缩
 *    Q:  决定jpg图片的绝对quality，把原图quality压到Q%
 * }
 */
function genSuffix(_options: I_THUMB_CONFIG) {
  const options: I_THUMB_CONFIG = {
    ...defaultOptions,
    ..._options,
  }
  const designWidth = 375
  let ratio: number
  if (options.fixedRatio) {
    ratio = options.fixedRatio
  } else {
    const maxRatio = options.maxRatio || 3
    ratio = Math.round((document.documentElement.clientWidth / designWidth) * deviceRatio || maxRatio)
    ratio = ratio > maxRatio ? maxRatio : ratio
  }
  if (options.w) {
    options.w = Math.round(options.w * ratio)
  }
  if (options.h) {
    options.h = Math.round(options.h * ratio)
  }
  if (options['at']) {
    return (
      'at_' +
      ['w', 'h']
        .map((i) => (options[i] ? options[i] + i : undefined))
        .filter((i) => i)
        .join('_')
    )
  }
  return ['w', 'h', 'e', 'c', 'l', 'q', 'Q']
    .map((i) => (options[i] ? options[i] + i : undefined))
    .filter((i) => i)
    .join('_')
}

function processGifSrc(src: string, options: { w?: number; h?: number }) {
  if (!options.w || !options.h) return src
  const strArr = src.split('//')
  if (strArr[0] && strArr[1]) {
    return strArr[0] + '//' + strArr[1].replace(/\//, `/${options.w}.${options.h}/`)
  }
  return src
}

export default function thumb(_src: string, options: I_THUMB_CONFIG = {}): string {
  if (!_src) {
    reportError({
      name: '图片地址为空',
    })
    return _src
  }
  if (_src.indexOf('knb-media://') === 0) return _src
  let imgSrc = `${_src.replace(/^(http:)?\/\//, 'https://')}`
  if (!/\/\/(p\d|img)\.meituan\.(net|com)\//.test(_src)) return imgSrc
  // TODO DDL 2025-01-01 gif 图片处理 https://km.sankuai.com/page/19266587
  if (/\.gif$/.test(imgSrc)) return processGifSrc(imgSrc, options)
  // 需要鉴权的图片会带?token=xxx，需处理参数，例如：
  // https://img.meituan.net/phxsensitive/56af6308f448e83cb6b0d3df7342420a922.png?token=1.1561007666.phxsensitive.2b788e622cbfdbe62501f30ae80b0293
  // 转换后 https://img.meituan.net/phxsensitive/56af6308f448e83cb6b0d3df7342420a922.png.webp@480w_360h_1e_1c_80q%7C480_360m?token=1.1561007666.phxsensitive.2b788e622cbfdbe62501f30ae80b0293
  let [src = '', search] = imgSrc.split('?')
  search = search ? '?' + search : ''
  // 线上环境点评App、iOS设备会强制HTTPS协议，此处统一采用HTTPS
  const suffix = options.suffix || genSuffix(options)
  const encodedSuffix = suffix ? '@' + encodeURIComponent(suffix) : ''
  const type = isWebpSupported(imgSrc)
  switch (type) {
    case webpSupportedStatus.domainNotSupport:
      imgSrc = src
      break
    case webpSupportedStatus.knbMediaSupport:
      if (options.noKnbMedia) {
        // 部分情况不能使用knb-media
        imgSrc = `${src}${encodedSuffix}${search}`
      } else {
        // 注：这边 @ 要一起 encode
        imgSrc = `knb-media://server?url=${encodeURIComponent(
          `${src}.webp${suffix ? encodeURIComponent('@' + suffix) : ''}${search || ''}`,
        )}`
      }
      break
    case webpSupportedStatus.support:
      imgSrc = `${src}.webp${encodedSuffix}${search}`
      break
    default:
      imgSrc = `${src}${encodedSuffix}${search}`
  }
  return imgSrc
}
